import React, { useCallback, useState, ChangeEvent, useEffect } from "react";
import { Card, Checkbox, Input, Row } from "antd";

import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { useTranslation } from "react-i18next";

import { VatRateWrapper } from "./styledComponents";
import RadioButtonGroup from "../RadioButtonGroup";
import { DateFiltersWrapper } from "../styledComponents";
import {
  Direction,
  KontaxTransactionFilterOptions,
  FilterSource,
  AccountOption,
} from "../../../types";
import AccountFilterDropdown from "./AccountFilterDropdown";
import VatPaymentFrequencyFilter from "./VatPaymentFrequencyFilter";
import PlanFilter from "./PlanFilter";
import AssigneeFilter from "../AssigneeFilter";
import DateFiltersPicker from "../DateFiltersPicker";
import DropdownMultiSelect, { QuickFilterOption } from "../DropdownMultiSelect";
import {
  incomingCategoriesRaw,
  outgoingCategoriesRaw,
  PRIVATE_CATEGORY_CODES,
  vatCategories as allVatCategories,
} from "../../../utils";
import FilterBoxCard from "../Card/FilterBoxCard";
import { getBerlinMomentTimezone } from "../helpers";
import CustomerSearchInput from "@/lib/customerSearch/CustomerSearchInput";
import ShowCount from "./ShowCount";

const { Search } = Input;

const VAT_CATEGORY_OPTIONS = allVatCategories.map(([value, label]) => ({
  value,
  label,
}));

const CATEGORY_OPTIONS = [
  ...incomingCategoriesRaw,
  ...outgoingCategoriesRaw,
].map(([value, label]) => ({ value, label }));

export enum Filter {
  STATUS = "status",
  ASSETS = "assets",
  VAT_PAYMENT_FREQUENCY = "vat-payment-frequency",
  PLAN = "plan",
  VAT_CATEGORIES = "vat-categories",
  CATEGORIES = "categories",
  SOURCE = "source",
  CATEGORIZED = "categorized",
  TRANSACTION_SEARCH = "transaction-search",
  ASSIGNEE = "assignee",
  ACCOUNT = "account",
}

const ACTION_REASON = "ACTION_REASON";

/**
 * The two quick filters above the divider in the category dropdown. Keys are
 * the payload `DropdownMultiSelect` reports them back under.
 */
export const CATEGORY_QUICK_FILTER = {
  UNCATEGORIZED: "uncategorized",
  PRIVATE: "private",
} as const;

type Props = {
  onChangeHandler: (
    filterOptions: Partial<KontaxTransactionFilterOptions>
  ) => void;
  hiddenFilters?: Filter[];
  showCount?: boolean;
  count?: number;
  fetchTransactionCount?: () => void;
  accountOptions?: AccountOption[];
} & KontaxTransactionFilterOptions;

const TransactionFilters = ({
  email,
  direction,
  source,
  dateFrom,
  dateTo,
  vatCategoryCodes,
  categories,
  onChangeHandler,
  hiddenFilters = [],
  plans,
  vatPaymentFrequency,
  categorized,
  showCount,
  count,
  fetchTransactionCount = () => {},
  search,
  excludeInternal,
  assigneeId,
  accountIds,
  accountOptions = [],
}: Props) => {
  const [searchInput, setSearchInput] = useState(search ?? "");
  const [includeInternalTransfersOnly, setIncludeInternalTransfersOnly] =
    useState(excludeInternal === false);
  const { t } = useTranslation("TransactionFilters");

  useEffect(() => {
    setSearchInput(search ?? "");
  }, [search]);

  const onEscalatedChange = (value: boolean | string) => {
    if (value === ACTION_REASON) {
      onChangeHandler({ actionReason: true });
    }

    if (typeof value == "boolean") {
      onChangeHandler({ escalated: value, actionReason: false });
    }
  };

  const includesFilter = useCallback(
    (filter: Filter) => !hiddenFilters.includes(filter),
    [hiddenFilters]
  );

  const selectedCategories = categories || [];

  /**
   * "Uncategorized" is standalone and exclusive: it maps to the backend's
   * `categorized: false`, which is ANDed with the category codes, so the two
   * cannot be combined. It is offered only where the Booking status radios are
   * hidden — otherwise two controls would drive the same backend filter.
   * "Private" is a plain shortcut over the three private codes and stays
   * combinable with the rest of the list.
   */
  const ownsCategorized = !includesFilter(Filter.CATEGORIZED);

  const categoryQuickFilters: QuickFilterOption[] = [
    ...(ownsCategorized
      ? [
          {
            key: CATEGORY_QUICK_FILTER.UNCATEGORIZED,
            label: "Uncategorized",
            checked: categorized === false,
            exclusive: true,
          },
        ]
      : []),
    {
      key: CATEGORY_QUICK_FILTER.PRIVATE,
      label: "Private",
      checked: PRIVATE_CATEGORY_CODES.every((code) =>
        selectedCategories.includes(code)
      ),
      values: PRIVATE_CATEGORY_CODES,
    },
  ];

  const onCategoriesChange = (
    values: CheckboxValueType[],
    quickFilters?: Record<string, boolean>
  ) => {
    onChangeHandler({
      categories: values,
      // Only ever `false` or cleared: where this branch runs, the Booking
      // status radios are hidden and "Uncategorized" is the sole control over
      // `categorized`, so there is no `true` here to preserve.
      ...(ownsCategorized && {
        categorized: quickFilters?.[CATEGORY_QUICK_FILTER.UNCATEGORIZED]
          ? false
          : undefined,
      }),
    });
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: "0" }}>
      <Row>
        <FilterBoxCard title="Email">
          {/* Resolves Public ID / Account ID to the customer's email
              first; the views keep receiving an email (SMT-1396). */}
          <CustomerSearchInput
            id="email"
            value={email}
            onSelect={(selected) => {
              onChangeHandler({ email: selected || "" });
            }}
          />
          {includesFilter(Filter.ASSIGNEE) && (
            <div style={{ marginTop: 8 }}>
              <AssigneeFilter
                value={assigneeId}
                onChange={(val) => onChangeHandler({ assigneeId: val })}
              />
            </div>
          )}
          {showCount ? (
            <ShowCount
              count={count}
              fetchTransactionCount={fetchTransactionCount}
            />
          ) : null}
        </FilterBoxCard>
        {includesFilter(Filter.CATEGORIZED) && (
          <FilterBoxCard data-test="bookingStatusRadios" title="Booking status">
            <RadioButtonGroup
              value={categorized}
              options={[
                { label: "All", value: undefined },
                {
                  label: "Not booked",
                  value: false,
                },
                {
                  label: "Booked, not verified",
                  value: true,
                },
              ]}
              onChange={(categorized?: boolean) => {
                onChangeHandler({ categorized });
              }}
            />
          </FilterBoxCard>
        )}
        <FilterBoxCard data-test="transactionDirectionTypeRadios" title="Type">
          <RadioButtonGroup
            value={direction}
            options={[
              { label: "All", value: undefined },
              {
                label: "Incoming",
                value: Direction.INCOMING,
              },
              {
                label: "Outgoing",
                value: Direction.OUTGOING,
              },
            ]}
            onChange={(direction: Direction) => {
              onChangeHandler({ direction });
            }}
          />
        </FilterBoxCard>
        {includesFilter(Filter.SOURCE) && (
          <FilterBoxCard data-test="transactionSourceRadios" title="Source">
            <RadioButtonGroup
              value={source}
              options={[
                { label: "All", value: undefined },
                {
                  label: "Bank",
                  value: FilterSource.BANK,
                },
                {
                  label: "External",
                  value: FilterSource.EXTERNAL,
                },
              ]}
              onChange={(source: FilterSource) => {
                onChangeHandler({ source });
              }}
            />
          </FilterBoxCard>
        )}

        {includesFilter(Filter.ACCOUNT) && email && (
          <FilterBoxCard
            data-test="transactionAccountFilter"
            title="Bank account"
          >
            <AccountFilterDropdown
              values={(accountIds as string[]) || []}
              options={accountOptions}
              onChange={(values) => onChangeHandler({ accountIds: values })}
            />
          </FilterBoxCard>
        )}
        {includesFilter(Filter.ASSETS) && (
          <FilterBoxCard data-test="invoicesRadios" title="Invoices">
            <RadioButtonGroup
              value={true}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              onChange={(assets: boolean) => {
                onChangeHandler({ assets });
              }}
            />
          </FilterBoxCard>
        )}
        {includesFilter(Filter.STATUS) && (
          <FilterBoxCard data-test="transactionStatusRadios" title="Status">
            <RadioButtonGroup
              value={false}
              options={[
                { label: "Normal", value: false },
                { label: "Escalated", value: true },
                { label: "User action required", value: ACTION_REASON },
              ]}
              onChange={(value: boolean | string) => {
                onEscalatedChange(value);
              }}
            />
          </FilterBoxCard>
        )}
        {includesFilter(Filter.VAT_PAYMENT_FREQUENCY) && (
          <VatPaymentFrequencyFilter
            onChangeHandler={onChangeHandler}
            value={vatPaymentFrequency}
          />
        )}
        {includesFilter(Filter.PLAN) && (
          <PlanFilter onChangeHandler={onChangeHandler} value={plans} />
        )}
        <FilterBoxCard title="Date filters">
          <DateFiltersWrapper data-test="transactionDateFilters">
            <DateFiltersPicker
              dateFrom={dateFrom ?? ""}
              dateTo={dateTo ?? ""}
              maxDate={getBerlinMomentTimezone().format()}
              onChange={onChangeHandler}
            />
          </DateFiltersWrapper>
        </FilterBoxCard>
        {(includesFilter(Filter.VAT_CATEGORIES) ||
          includesFilter(Filter.CATEGORIES)) && (
          <FilterBoxCard
            data-test="transactionCategoryFilters"
            title="Category filters"
          >
            {includesFilter(Filter.VAT_CATEGORIES) && (
              <VatRateWrapper>
                <DropdownMultiSelect
                  name="vat-category"
                  placeholder="All VAT categories"
                  values={vatCategoryCodes || []}
                  options={VAT_CATEGORY_OPTIONS}
                  disabled={includeInternalTransfersOnly}
                  onChange={(values: CheckboxValueType[]) =>
                    onChangeHandler({ vatCategoryCodes: values })
                  }
                  withFilter={false}
                />
              </VatRateWrapper>
            )}
            {includesFilter(Filter.CATEGORIES) && (
              <DropdownMultiSelect
                name="category"
                placeholder="All categories"
                values={selectedCategories}
                options={CATEGORY_OPTIONS}
                disabled={includeInternalTransfersOnly}
                onChange={onCategoriesChange}
                quickFilters={categoryQuickFilters}
                moveSelectedToTheTop
                withCheckAll
              />
            )}
            <div style={{ paddingTop: 10, paddingBottom: 10 }}>
              <Checkbox
                checked={includeInternalTransfersOnly}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setIncludeInternalTransfersOnly(checked);

                  // The quick filters are disabled while this is on, so clear
                  // them too rather than leaving one active but unreachable.
                  const clearCategorized = ownsCategorized && {
                    categorized: undefined,
                  };

                  if (checked) {
                    onChangeHandler({
                      excludeInternal: false,
                      vatCategoryCodes: [],
                      categories: ["0000"],
                      ...clearCategorized,
                    });
                  } else {
                    onChangeHandler({
                      excludeInternal: undefined,
                      vatCategoryCodes: [],
                      categories: [],
                      ...clearCategorized,
                    });
                  }
                }}
              >
                {t("internalTransferLabel")}
              </Checkbox>
            </div>
          </FilterBoxCard>
        )}
        {includesFilter(Filter.TRANSACTION_SEARCH) && (
          <FilterBoxCard title="Transaction Search">
            <Search
              value={searchInput}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSearchInput(event.target.value);
              }}
              onSearch={(value) => {
                onChangeHandler({ search: value });
              }}
              placeholder="Search amount, name or description"
              allowClear
              enterButton="Search"
              disabled={!email}
            />
          </FilterBoxCard>
        )}
      </Row>
    </Card>
  );
};

export default TransactionFilters;
